import {
  Button,
  InlineNotification,
  Link,
  OverflowMenu,
  OverflowMenuItem,
  SkeletonText,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@carbon/react';
import { Add, Launch, TrashCan } from '@carbon/icons-react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useAdmin } from '../adminContext';
import { BackLink } from '../components/BackLink';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { RowAction } from '../components/RowAction';
import { StatusTag } from '../components/StatusTag';
import { asProblem, problemMessage } from '../problems';
import { detailRoute } from '../routes';
import { hasPermission } from '../session';
import {
  DELIVERY_FINAL,
  ORDER_PERMISSIONS,
  ordersKey,
  useFormat,
  useOrderSetup,
  useOrdersApi,
  type OrdersMode,
} from './model';
import { OrderKitchenSection } from '../kitchen/OrderKitchenSection';
import { useCanReadKitchen } from '../kitchen/model';
import { OrderEinvoiceFact } from './OrderEinvoiceFact';
import { OrderStatusTags } from './OrderStatusTag';
import {
  AddItemsDialog,
  ChargeDialog,
  DeliveryStatusDialog,
  DetailsDialog,
  DiscountDialog,
  DispatchDialog,
  EditLineDialog,
  ChangeMethodDialog,
  CorrectGuestsDialog,
  methodCorrectable,
  PaymentDialog,
  RefundDialog,
  SaleTypeDialog,
  VoidDialog,
  VoidRedoDialog,
} from './OrderDialogs';
import type { Delivery, OrderDetail, OrderLine, Payment } from './ordersApi';

const NOTICE_MS = 8000;

type Dialog =
  | { kind: 'addItems' | 'details' | 'saleType' | 'discount' | 'charge' | 'payment' | 'voidOrder' | 'dispatch' | 'correctGuests' | 'voidRedo' }
  | { kind: 'editLine' | 'voidLine'; line: OrderLine }
  | { kind: 'refund' | 'changeMethod'; payment: Payment }
  | { kind: 'deliveryStatus'; delivery: Delivery };

/** One order: totals, items, payments, deliveries and history. At the store it can be changed while unpaid. */
export function OrderDetailPage({ mode }: { mode: OrdersMode }) {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const api = useOrdersApi();
  const order = useQuery({ queryKey: [...ordersKey, 'detail', id], queryFn: () => api.get(id) });

  return (
    <section className="pos-admin-page">
      <BackLink to="/orders">{t('orders.back')}</BackLink>
      {order.error ? (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(order.error))}
        />
      ) : !order.data ? (
        <SkeletonText heading paragraph lineCount={8} />
      ) : (
        <Detail order={order.data} mode={mode} />
      )}
    </section>
  );
}

function Detail({ order, mode }: { order: OrderDetail; mode: OrdersMode }) {
  const { t } = useTranslation();
  const { session, resources } = useAdmin();
  const format = useFormat();
  const { saleTypeName, reasonName, menuName, priceListName } = useOrderSetup();
  const [dialog, setDialog] = useState<Dialog>();
  const [notice, setNotice] = useState<string>();
  // A confirmation is transient: it clears itself, like the error notifications it sits beside it has no close button.
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(undefined), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);
  const summary = order.summary;
  const money = (value: number | string) => format.money(value, summary.currencyCode);
  const can = (permission: string) => mode === 'store' && hasPermission(session, permission);
  const editable = summary.isEditable && can(ORDER_PERMISSIONS.manage);
  // A paid bill corrected the same day (ADR 0135): the store refuses another day's, and asks a manager when the user may not.
  const correctable = summary.orderStatus === 'completed' && summary.origin !== 'online' && can(ORDER_PERMISSIONS.manage);
  const navigate = useNavigate();
  const close = () => setDialog(undefined);
  const canReadKitchen = useCanReadKitchen(mode);
  const address = order.deliveryAddress as Record<string, string | null> | null;

  const facts: [string, ReactNode][] = [
    [t('orders.columns.saleType'), saleTypeName(summary.saleTypeId, summary.saleTypeCode)],
    [t('orders.summary.menu'), menuName(order.menuId) ?? '—'],
    [t('orders.summary.priceList'), priceListName(order.priceListId) ?? '—'],
    [t('orders.summary.openedAt'), format.dateTime(summary.openedAt)],
    ...(summary.paidAt
      ? ([[t('orders.summary.paidAt'), format.dateTime(summary.paidAt)]] as [string, ReactNode][])
      : []),
    ...(summary.guestCount != null
      ? ([[t('orders.summary.guests'), String(summary.guestCount)]] as [string, ReactNode][])
      : []),
    ...(address
      ? ([
          [
            t('orders.summary.customer'),
            [address.name, address.phone, address.street, address.ward, address.province].filter(Boolean).join(', '),
          ],
        ] as [string, ReactNode][])
      : []),
    // The member as attached at the till (ADR 0044); at HO the account is one tap away. Points are worked out at HO only.
    ...(order.member
      ? ([
          [
            t('orders.summary.member'),
            <>
              {[
                order.member.accountNo,
                order.member.name,
                order.member.schemeName,
                order.member.maskedPhone,
                order.member.maskedCard,
              ]
                .filter(Boolean)
                .join(' · ')}
              {resources['membership.member_account'] && (
                <>
                  {' '}
                  <RouterLink to={detailRoute('membership.member_account', order.member.accountId)}>
                    {t('orders.summary.openMember')}
                  </RouterLink>
                </>
              )}
            </>,
          ],
        ] as [string, ReactNode][])
      : []),
    ...(summary.promisedAt
      ? ([[t('orders.summary.promisedAt'), format.dateTime(summary.promisedAt)]] as [string, ReactNode][])
      : []),
    ...(order.customerNote ? ([[t('orders.summary.customerNote'), order.customerNote]] as [string, ReactNode][]) : []),
    ...(order.kitchenNote ? ([[t('orders.summary.kitchenNote'), order.kitchenNote]] as [string, ReactNode][]) : []),
    ...(order.voidReasonCodeId
      ? ([[t('orders.void.reason'), reasonName(order.voidReasonCodeId) ?? '']] as [string, ReactNode][])
      : []),
    // The e-invoice HO issues for every completed order (ADR 0048, FR-EIN-04; ADR 0088): asked from HO, so only at the store.
    ...(mode === 'store' && summary.orderStatus === 'completed'
      ? ([[t('orders.einvoice.label'), <OrderEinvoiceFact key="einvoice" orderId={summary.id} />]] as [
          string,
          ReactNode,
        ][])
      : []),
  ];

  const totals: [string, string][] = [
    [t('orders.summary.subtotal'), money(order.subtotal)],
    ...(Number(order.discountTotal)
      ? ([[t('orders.summary.discounts'), `−${money(order.discountTotal)}`]] as [string, string][])
      : []),
    ...(Number(order.chargeTotal)
      ? ([[t('orders.summary.charges'), money(order.chargeTotal)]] as [string, string][])
      : []),
    [t(order.isTaxInclusive ? 'orders.summary.taxIncluded' : 'orders.summary.tax'), money(order.taxTotal)],
    ...(Number(order.roundingAmount)
      ? ([[t('orders.summary.rounding'), money(order.roundingAmount)]] as [string, string][])
      : []),
  ];

  return (
    <>
      <PageHeader
        title={summary.orderNumber}
        description={mode === 'ho' ? t('orders.readOnly') : undefined}
        tag={<OrderStatusTags order={summary} />}
        actions={
          <>
            {summary.isEditable && can(ORDER_PERMISSIONS.record) && Number(summary.balance) > 0 && (
              <Button onClick={() => setDialog({ kind: 'payment' })}>{t('orders.actions.recordPayment')}</Button>
            )}
            {editable && (
              <Button kind="tertiary" renderIcon={Add} onClick={() => setDialog({ kind: 'addItems' })}>
                {t('orders.actions.addItems')}
              </Button>
            )}
            {editable && (
              <OverflowMenu aria-label={t('orders.actions.more')} iconDescription={t('orders.actions.more')} flipped>
                <OverflowMenuItem
                  itemText={t('orders.actions.editDetails')}
                  onClick={() => setDialog({ kind: 'details' })}
                />
                <OverflowMenuItem
                  itemText={t('orders.actions.changeSaleType')}
                  onClick={() => setDialog({ kind: 'saleType' })}
                />
                {can(ORDER_PERMISSIONS.discount) && (
                  <OverflowMenuItem
                    itemText={t('orders.actions.addDiscount')}
                    onClick={() => setDialog({ kind: 'discount' })}
                  />
                )}
                {can(ORDER_PERMISSIONS.charge) && (
                  <OverflowMenuItem
                    itemText={t('orders.actions.addCharge')}
                    onClick={() => setDialog({ kind: 'charge' })}
                  />
                )}
                {Number(summary.paidTotal) === 0 && (
                  <OverflowMenuItem
                    isDelete
                    hasDivider
                    itemText={t('orders.actions.voidOrder')}
                    onClick={() => setDialog({ kind: 'voidOrder' })}
                  />
                )}
              </OverflowMenu>
            )}
            {correctable && (
              <OverflowMenu aria-label={t('orders.correction.menu')} iconDescription={t('orders.correction.menu')} flipped>
                <OverflowMenuItem itemText={t('orders.correction.guests')} onClick={() => setDialog({ kind: 'correctGuests' })} />
                <OverflowMenuItem
                  isDelete
                  hasDivider
                  itemText={t('orders.correction.voidRedo')}
                  onClick={() => setDialog({ kind: 'voidRedo' })}
                />
              </OverflowMenu>
            )}
          </>
        }
      />
      {notice && <InlineNotification kind="success" role="status" lowContrast hideCloseButton title={notice} />}

      <div className="pos-orders__overview">
        <StructuredListWrapper isCondensed aria-label={summary.orderNumber}>
          <StructuredListBody>
            {facts.map(([label, value]) => (
              <StructuredListRow key={label}>
                <StructuredListCell noWrap>{label}</StructuredListCell>
                <StructuredListCell>{value}</StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
        <dl className="pos-orders__totals">
          {totals.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd className="pos-admin-page__number">{value}</dd>
            </div>
          ))}
          <div className="pos-orders__totals-total">
            <dt>{t('orders.summary.total')}</dt>
            <dd className="pos-admin-page__number">{money(summary.total)}</dd>
          </div>
          <div>
            <dt>{t('orders.summary.paid')}</dt>
            <dd className="pos-admin-page__number">{money(summary.paidTotal)}</dd>
          </div>
          {summary.isEditable && (
            <div className="pos-orders__totals-total">
              <dt>{t('orders.summary.balance')}</dt>
              <dd className="pos-admin-page__number">{money(summary.balance)}</dd>
            </div>
          )}
        </dl>
      </div>

      <Tabs>
        <TabList aria-label={summary.orderNumber} contained>
          <Tab>{t('orders.tabs.items')}</Tab>
          <Tab>{t('orders.tabs.payments')}</Tab>
          <Tab>{t('orders.tabs.delivery')}</Tab>
          <Tab>{t('orders.tabs.history')}</Tab>
          {canReadKitchen ? <Tab>{t('admin.kitchen.order.tab')}</Tab> : null}
        </TabList>
        <TabPanels>
          <TabPanel>
            <LinesTab order={order} editable={editable} onDialog={setDialog} />
          </TabPanel>
          <TabPanel>
            <PaymentsTab
              order={order}
              canRefund={can(ORDER_PERMISSIONS.refund)}
              canCorrect={correctable}
              onRefund={(payment) => setDialog({ kind: 'refund', payment })}
              onChangeMethod={(payment) => setDialog({ kind: 'changeMethod', payment })}
            />
          </TabPanel>
          <TabPanel>
            <DeliveriesTab order={order} canManage={can(ORDER_PERMISSIONS.delivery)} onDialog={setDialog} />
          </TabPanel>
          <TabPanel>
            <HistoryTab order={order} />
          </TabPanel>
          {canReadKitchen ? (
            <TabPanel>
              <OrderKitchenSection orderId={summary.id} mode={mode} />
            </TabPanel>
          ) : null}
        </TabPanels>
      </Tabs>

      {dialog?.kind === 'addItems' && <AddItemsDialog order={order} onClose={close} />}
      {dialog?.kind === 'details' && <DetailsDialog order={order} onClose={close} />}
      {dialog?.kind === 'saleType' && <SaleTypeDialog order={order} onClose={close} />}
      {dialog?.kind === 'discount' && <DiscountDialog order={order} onClose={close} />}
      {dialog?.kind === 'charge' && <ChargeDialog order={order} onClose={close} />}
      {dialog?.kind === 'payment' && <PaymentDialog order={order} onClose={close} onDone={setNotice} />}
      {dialog?.kind === 'voidOrder' && <VoidDialog order={order} onClose={close} />}
      {dialog?.kind === 'editLine' && <EditLineDialog order={order} line={dialog.line} onClose={close} />}
      {dialog?.kind === 'voidLine' && <VoidDialog order={order} line={dialog.line} onClose={close} />}
      {dialog?.kind === 'refund' && <RefundDialog order={order} payment={dialog.payment} onClose={close} />}
      {dialog?.kind === 'changeMethod' && (
        <ChangeMethodDialog order={order} payment={dialog.payment} onClose={close} onDone={setNotice} />
      )}
      {dialog?.kind === 'correctGuests' && <CorrectGuestsDialog order={order} onClose={close} onDone={setNotice} />}
      {dialog?.kind === 'voidRedo' && (
        <VoidRedoDialog
          order={order}
          onClose={close}
          onRedone={(redoId) => {
            if (redoId) void navigate(`/orders/${redoId}`);
            else setNotice(t('orders.correction.voidedNoRedo'));
          }}
        />
      )}
      {dialog?.kind === 'dispatch' && <DispatchDialog order={order} onClose={close} />}
      {dialog?.kind === 'deliveryStatus' && (
        <DeliveryStatusDialog order={order} delivery={dialog.delivery} onClose={close} />
      )}
    </>
  );
}

/** Each line followed by its deal's items, in order; an item whose deal isn't there comes last. */
function byDeal<T extends { id: string; parentLineId?: string | null }>(lines: readonly T[]): T[] {
  const tops = lines.filter((l) => !l.parentLineId);
  const ids = new Set(tops.map((l) => l.id));
  return [
    ...tops.flatMap((top) => [top, ...lines.filter((l) => l.parentLineId === top.id)]),
    ...lines.filter((l) => l.parentLineId && !ids.has(l.parentLineId)),
  ];
}

function LinesTab({ order, editable, onDialog }: { order: OrderDetail; editable: boolean; onDialog(d: Dialog): void }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const queryClient = useQueryClient();
  const format = useFormat();
  const { name } = useOrderSetup();
  const money = (value: number | string) => format.money(value, order.summary.currencyCode);
  const remove = useMutation({
    mutationFn: (target: { discountId: string } | { chargeId: string }) =>
      'discountId' in target
        ? api.removeDiscount(order.summary, target.discountId)
        : api.removeCharge(order.summary, target.chargeId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ordersKey }),
  });

  return (
    <div className="pos-orders__tab">
      {remove.error && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(remove.error))}
        />
      )}
      <TableContainer>
        <Table aria-label={t('orders.tabs.items')}>
          <TableHead>
            <TableRow>
              <TableHeader>{t('orders.lines.item')}</TableHeader>
              <TableHeader>{t('orders.lines.unit')}</TableHeader>
              <TableHeader>{t('orders.lines.quantity')}</TableHeader>
              <TableHeader>{t('orders.lines.unitPrice')}</TableHeader>
              <TableHeader>{t('orders.lines.options')}</TableHeader>
              <TableHeader>{t('orders.lines.discount')}</TableHeader>
              <TableHeader>{t('orders.lines.amount')}</TableHeader>
              <TableHeader>{t('orders.lines.tax')}</TableHeader>
              <TableHeader>{t('orders.lines.taxAmount')}</TableHeader>
              <TableHeader>{t('orders.lines.status')}</TableHeader>
              {editable && <TableHeader aria-label={t('admin.actions')} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {byDeal(order.lines).map((line) => {
              const voided = line.status === 'voided';
              // Each row reads Qty x Unit price + Options - Discount = Amount. A deal's item sits under its deal: its amount is its
              // share of the deal price, already in the deal's amount, so it is shown in brackets (ADR 0034).
              const dealItem = Boolean(line.parentLineId);
              const classes = [voided ? 'pos-orders__line--voided' : '', dealItem ? 'pos-orders__line--deal-item' : '']
                .filter(Boolean)
                .join(' ');
              return (
                <TableRow key={line.id} className={classes || undefined}>
                  <TableCell>
                    <p>{line.name}</p>
                    {line.modifiers.length > 0 && (
                      <p className="pos-orders__muted">
                        {line.modifiers
                          .map((m) => (Number(m.unitPrice) > 0 ? `${m.name} (+${money(m.unitPrice)})` : m.name))
                          .join(', ')}
                      </p>
                    )}
                    {line.note && <p className="pos-orders__muted">{line.note}</p>}
                  </TableCell>
                  {/* The unit's code (a deal's own line has none) and the VAT rate the line was sold at. */}
                  <TableCell>{line.uomCode ?? ''}</TableCell>
                  <TableCell className="pos-admin-page__number">{format.number(line.quantity)}</TableCell>
                  {/* A deal's item has no price of its own: only an upcharge for choosing it. */}
                  <TableCell className="pos-admin-page__number">
                    {dealItem ? (Number(line.unitPrice) ? `+${money(line.unitPrice)}` : '') : money(line.unitPrice)}
                  </TableCell>
                  <TableCell className="pos-admin-page__number">
                    {Number(line.modifierTotal) ? money(line.modifierTotal) : ''}
                  </TableCell>
                  <TableCell className="pos-admin-page__number">
                    {Number(line.discountTotal) ? `−${money(line.discountTotal)}` : ''}
                  </TableCell>
                  <TableCell className="pos-admin-page__number">
                    {voided ? (
                      ''
                    ) : dealItem ? (
                      <span className="pos-orders__muted">({money(line.lineTotal)})</span>
                    ) : (
                      money(line.lineTotal)
                    )}
                  </TableCell>
                  {/* The rate the line was sold at and the tax on it; a deal's tax is the whole deal's (its rate is its items'). */}
                  <TableCell className="pos-admin-page__number">
                    {line.taxRate == null ? '' : `${format.number(line.taxRate)}%`}
                  </TableCell>
                  <TableCell className="pos-admin-page__number">
                    {voided || (line.taxRate == null && !Number(line.taxAmount)) ? '' : money(line.taxAmount)}
                  </TableCell>
                  <TableCell>
                    <StatusTag status={voided ? 'voided' : line.status}>
                      {t(`orders.lines.statusValues.${line.status}` as I18nKey, { defaultValue: line.status })}
                    </StatusTag>
                  </TableCell>
                  {editable && (
                    <TableCell className="pos-admin-page__actions">
                      {!voided && (
                        <OverflowMenu
                          aria-label={t('orders.actions.more')}
                          iconDescription={t('orders.actions.more')}
                          flipped
                        >
                          {line.status === 'new' && (
                            <OverflowMenuItem
                              itemText={t('orders.actions.editLine')}
                              onClick={() => onDialog({ kind: 'editLine', line })}
                            />
                          )}
                          <OverflowMenuItem
                            isDelete
                            itemText={t('orders.actions.voidLine')}
                            onClick={() => onDialog({ kind: 'voidLine', line })}
                          />
                        </OverflowMenu>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {(order.discounts.length > 0 || order.charges.length > 0) && (
        <ul className="pos-orders__adjustments">
          {order.discounts.map((d) => (
            <li key={d.id}>
              <span>
                {t('orders.discounts.title')}: {d.name}
                {d.method === 'percent' ? ` (${format.number(d.value)}%)` : ''}
                {d.orderLineId ? ` · ${order.lines.find((l) => l.id === d.orderLineId)?.name ?? ''}` : ''}
                {d.appliesAfterTax ? ` · ${t('orders.afterTax')}` : ''}
              </span>
              <span className="pos-admin-page__number">−{money(d.amount)}</span>
              {editable && (
                <RowAction
                  action={t('orders.actions.remove')}
                  record={d.name}
                  icon={TrashCan}
                  onClick={() => remove.mutate({ discountId: d.id })}
                />
              )}
            </li>
          ))}
          {order.charges.map((c) => (
            <li key={c.id}>
              <span>
                {t('orders.charges.title')}: {name(c.chargeTypeId, c.name)}
                {c.appliesAfterTax ? ` · ${t('orders.afterTax')}` : ''}
              </span>
              <span className="pos-admin-page__number">{money(c.amount)}</span>
              {/* The delivery fee (no charge type) follows the delivery (FR-DSC-12). */}
              {editable && c.chargeTypeId && (
                <RowAction
                  action={t('orders.actions.remove')}
                  record={name(c.chargeTypeId, c.name)}
                  icon={TrashCan}
                  onClick={() => remove.mutate({ chargeId: c.id })}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {order.taxes.length > 0 && (
        <p className="pos-orders__muted">
          {order.taxes.map((tax) => `${tax.name}: ${money(tax.taxAmount)}`).join(' · ')}
        </p>
      )}
    </div>
  );
}

function PaymentsTab({
  order,
  canRefund,
  canCorrect,
  onRefund,
  onChangeMethod,
}: {
  order: OrderDetail;
  canRefund: boolean;
  canCorrect: boolean;
  onRefund(p: Payment): void;
  onChangeMethod(p: Payment): void;
}) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const { paymentMethodName, reasonName } = useOrderSetup();
  const payments = useQuery({
    queryKey: [...ordersKey, 'payments', order.summary.id],
    queryFn: () => api.payments(order.summary.id),
  });
  if (payments.error)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(payments.error))}
      />
    );
  if (!payments.data) return <SkeletonText paragraph lineCount={3} />;
  if (payments.data.length === 0) return <EmptyState headingLevel={2} heading={t('orders.payments.empty')} />;

  return (
    <div className="pos-orders__tab">
      <TableContainer>
        <Table aria-label={t('orders.tabs.payments')}>
          <TableHead>
            <TableRow>
              <TableHeader>{t('orders.payments.paidAt')}</TableHeader>
              <TableHeader>{t('orders.payments.kind')}</TableHeader>
              <TableHeader>{t('orders.payments.method')}</TableHeader>
              <TableHeader>{t('orders.payments.amount')}</TableHeader>
              <TableHeader>{t('orders.payments.tendered')}</TableHeader>
              <TableHeader>{t('orders.payments.change')}</TableHeader>
              <TableHeader>{t('orders.payments.reference')}</TableHeader>
              <TableHeader>{t('orders.payments.receivedBy')}</TableHeader>
              <TableHeader>{t('orders.payments.refundable')}</TableHeader>
              {(canRefund || canCorrect) && <TableHeader aria-label={t('admin.actions')} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.data.map((p) => {
              const money = (value: number | string | null) =>
                value == null ? '' : format.money(value, p.currencyCode);
              const refund = p.kind === 'refund';
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <RouterLink to={`/sales/payments/${p.id}`}>{format.dateTime(p.paidAt)}</RouterLink>
                  </TableCell>
                  <TableCell>
                    <StatusTag status={p.kind}>
                      {t(`orders.payments.kinds.${p.kind}` as I18nKey, { defaultValue: p.kind })}
                    </StatusTag>
                    {refund && p.reasonCodeId && <p className="pos-orders__muted">{reasonName(p.reasonCodeId)}</p>}
                  </TableCell>
                  <TableCell>
                    {paymentMethodName(p.paymentMethodId) ??
                      t(`orders.methodTypes.${p.methodType}` as I18nKey, { defaultValue: p.methodType })}
                    {p.providerName && <p className="pos-orders__muted">{p.providerName}</p>}
                  </TableCell>
                  <TableCell className="pos-admin-page__number">
                    {refund ? `−${money(p.amount)}` : money(p.amount)}
                  </TableCell>
                  <TableCell className="pos-admin-page__number">{money(p.tenderedAmount)}</TableCell>
                  <TableCell className="pos-admin-page__number">
                    {Number(p.changeAmount) ? money(p.changeAmount) : ''}
                  </TableCell>
                  <TableCell>
                    {[p.providerReference, p.cardLast4 && `•••• ${p.cardLast4}`].filter(Boolean).join(' ')}
                  </TableCell>
                  <TableCell>{p.receivedByName ?? ''}</TableCell>
                  <TableCell className="pos-admin-page__number">{refund ? '' : money(p.refundable)}</TableCell>
                  {(canRefund || canCorrect) && (
                    <TableCell className="pos-admin-page__actions">
                      {canCorrect && methodCorrectable(p) && (
                        <Button kind="ghost" onClick={() => onChangeMethod(p)}>
                          {t('orders.correction.changeMethod')}
                        </Button>
                      )}
                      {canRefund && !refund && Number(p.refundable) > 0 && (
                        <Button kind="ghost" onClick={() => onRefund(p)}>
                          {t('orders.actions.refund')}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

function DeliveriesTab({
  order,
  canManage,
  onDialog,
}: {
  order: OrderDetail;
  canManage: boolean;
  onDialog(d: Dialog): void;
}) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const { setup } = useOrderSetup();
  const deliveries = useQuery({
    queryKey: [...ordersKey, 'deliveries', order.summary.id],
    queryFn: () => api.deliveries(order.summary.id),
  });
  const isDelivery = setup?.saleTypes.find((s) => s.id === order.summary.saleTypeId)?.baseBehavior === 'delivery';
  const list = deliveries.data ?? [];
  const inProgress = list.some((d) => !DELIVERY_FINAL.includes(d.status));
  const canDispatch =
    canManage && isDelivery && !inProgress && !['voided', 'merged', 'cancelled'].includes(order.summary.orderStatus);

  if (deliveries.error)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(deliveries.error))}
      />
    );
  if (!deliveries.data) return <SkeletonText paragraph lineCount={3} />;

  return (
    <div className="pos-orders__tab">
      {canDispatch && (
        <Button kind="tertiary" renderIcon={Add} onClick={() => onDialog({ kind: 'dispatch' })}>
          {t('orders.actions.dispatch')}
        </Button>
      )}
      {list.length === 0 && (
        <EmptyState heading={t(isDelivery ? 'orders.delivery.empty' : 'orders.delivery.notDelivery')} />
      )}
      {list.map((delivery, index) => {
        const address = delivery.address as Record<string, string | null>;
        return (
          <article key={delivery.id} className="pos-orders__delivery">
            <header className="pos-orders__delivery-header">
              <h2 className="pos-orders__section-title">{t('orders.delivery.attempt', { number: index + 1 })}</h2>
              <StatusTag status={delivery.status}>
                {t(`orders.delivery.statuses.${delivery.status}` as I18nKey, { defaultValue: delivery.status })}
              </StatusTag>
              <RouterLink to={`/sales/shipping/${delivery.id}`}>{t('transactions.detail.open')}</RouterLink>
              {canManage && delivery.nextStatuses.length > 0 && (
                <Button kind="ghost" onClick={() => onDialog({ kind: 'deliveryStatus', delivery })}>
                  {t('orders.actions.updateStatus')}
                </Button>
              )}
            </header>
            <dl className="pos-orders__facts">
              <dt>{t('orders.delivery.method')}</dt>
              <dd>
                {t(`orders.delivery.methods.${delivery.method}` as I18nKey, { defaultValue: delivery.method })}
                {delivery.providerCode ? ` · ${delivery.providerName ?? delivery.providerCode}` : ''}
                {delivery.providerReference ? ` · ${delivery.providerReference}` : ''}
              </dd>
              {delivery.bookingMode && (
                <>
                  <dt>{t('orders.delivery.booking')}</dt>
                  <dd>
                    {t(`orders.delivery.bookingModes.${delivery.bookingMode}` as I18nKey, {
                      defaultValue: delivery.bookingMode,
                    })}
                  </dd>
                </>
              )}
              {delivery.etaMinutes != null && (
                <>
                  <dt>{t('orders.delivery.eta')}</dt>
                  <dd>{t('orders.delivery.etaMinutes', { minutes: Number(delivery.etaMinutes) })}</dd>
                </>
              )}
              {delivery.driverName && (
                <>
                  <dt>{t('orders.delivery.driverName')}</dt>
                  <dd>{[delivery.driverName, delivery.driverPhone].filter(Boolean).join(' · ')}</dd>
                </>
              )}
              <dt>{t('orders.delivery.address')}</dt>
              <dd>
                {[address.name, address.phone, address.street, address.ward, address.province, address.note]
                  .filter(Boolean)
                  .join(', ')}
              </dd>
              {Number(delivery.fee) > 0 && (
                <>
                  <dt>{t('orders.delivery.fee')}</dt>
                  <dd className="pos-admin-page__number">{format.money(delivery.fee, order.summary.currencyCode)}</dd>
                </>
              )}
              {delivery.failureReason && (
                <>
                  <dt>{t('orders.delivery.failureReason')}</dt>
                  <dd>{delivery.failureReason}</dd>
                </>
              )}
              {delivery.trackingUrl && (
                <>
                  <dt>{t('orders.delivery.trackingUrl')}</dt>
                  <dd>
                    <Link href={delivery.trackingUrl} target="_blank" rel="noreferrer" renderIcon={Launch}>
                      {t('orders.delivery.track')}
                    </Link>
                  </dd>
                </>
              )}
            </dl>
            <ol className="pos-orders__timeline">
              {delivery.events.map((e) => (
                <li key={e.id}>
                  <span className="pos-orders__timeline-time">{format.dateTime(e.occurredAt)}</span>
                  <span>
                    {t(`orders.delivery.statuses.${e.status}` as I18nKey, { defaultValue: e.status })}
                    {e.note ? ` · ${e.note}` : ''}
                  </span>
                  {e.userName && (
                    <span className="pos-orders__muted">{t('orders.history.by', { name: e.userName })}</span>
                  )}
                </li>
              ))}
            </ol>
          </article>
        );
      })}
    </div>
  );
}

function HistoryTab({ order }: { order: OrderDetail }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const events = useQuery({
    queryKey: [...ordersKey, 'events', order.summary.id],
    queryFn: () => api.events(order.summary.id),
  });
  if (events.error)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(events.error))}
      />
    );
  if (!events.data) return <SkeletonText paragraph lineCount={4} />;
  if (events.data.length === 0) return <EmptyState headingLevel={2} heading={t('orders.history.empty')} />;

  return (
    <ol className="pos-orders__timeline pos-orders__tab">
      {events.data.map((e) => (
        <li key={e.id}>
          <span className="pos-orders__timeline-time">{format.dateTime(e.occurredAt)}</span>
          <span>
            <strong>{t(`orders.history.events.${e.eventType}` as I18nKey, { defaultValue: e.eventType })}</strong>
            <EventDetail payload={e.payload as Record<string, unknown>} currency={order.summary.currencyCode} />
          </span>
          <span className="pos-orders__muted">
            {t('orders.history.by', { name: e.userName ?? t('orders.history.unknownUser') })}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** The useful part of an event's payload: names, quantities, amounts, status moves. */
function EventDetail({ payload, currency }: { payload: Record<string, unknown>; currency: string }) {
  const { t } = useTranslation();
  const format = useFormat();
  const parts: string[] = [];
  const text = (key: string) => (typeof payload[key] === 'string' ? (payload[key] as string) : undefined);
  const amount = (key: string) =>
    typeof payload[key] === 'number' ? format.money(payload[key] as number, currency) : undefined;
  const status = (s: string) => t(`orders.delivery.statuses.${s}` as I18nKey, { defaultValue: s });
  const [name, method, paid, from, to, note, failure, total] = [
    text('name'),
    text('method'),
    amount('amount'),
    text('from'),
    text('to'),
    text('note'),
    text('failureReason'),
    amount('total'),
  ];
  if (name) parts.push(typeof payload.quantity === 'number' ? `${format.number(payload.quantity)} × ${name}` : name);
  if (method && paid) parts.push(`${method} ${paid}`);
  if (from && to) parts.push(`${status(from)} → ${status(to)}`);
  if (note) parts.push(note);
  if (failure) parts.push(failure);
  if (total && parts.length === 0) parts.push(total);
  return parts.length ? <span className="pos-orders__muted"> · {parts.join(' · ')}</span> : null;
}
